/**
 * Box scores: who actually scored the points.
 *
 * Yahoo records a matchup twice. The standings and matchup lists carry two team totals, which is
 * what every other board in this app is built on; the matchup page itself carries the lines behind
 * those totals — nine starters and eight bench players a side, with the points each of them put up.
 * The second one is the interesting one and it is the one nobody had, because it lives on roughly
 * 1,880 separate pages behind a login that starts refusing after about forty rapid requests.
 *
 * Two ways in, deliberately:
 *
 *   Bundled. Once a season has been collected it ships with the build, like every other archive.
 *   That is the end state and it needs no network, no storage and no browser.
 *
 *   Loaded. A scrape produces one JSON file, and a commissioner can load it straight into the app
 *   without waiting for a rebuild. Held in memory and reported as loaded rather than bundled, so
 *   nobody mistakes a file on one laptop for something the league has.
 *
 * The team TOTAL is the cross-check. Every scraped game carries the total Yahoo printed on the
 * matchup page, and the app already knows what that total should be from an independent scrape of
 * the standings. Where they disagree the box score is marked rather than trusted — a parser that
 * quietly drops a bench row would otherwise look exactly like a manager who left points on it.
 */

import bundled from '../data/box-scores.json';

/** [name, position, points]. Position is BN for a bench line. */
export type Line = [string, string, number | null];

export interface BoxSide {
  /** Yahoo's team id for that season. */
  id: number;
  team: string;
  /** The total Yahoo printed, which is what the starters should add up to. */
  pts: number | null;
  st: Line[];
  bn: Line[];
}

export interface BoxGame {
  s: number;
  w: number;
  a: BoxSide;
  b: BoxSide;
}

interface BoxFile { version: number; source: string; games: BoxGame[]; errors?: unknown[] }

/**
 * The bundled form, dictionary-encoded.
 *
 * The scrape writes every player's name in full, eighty thousand times, and this app ships as one
 * HTML file that people open on a phone at a draft party. Interning the names and flattening each
 * line to three numbers halves the payload for identical data. Points are hundredths so the JSON
 * carries integers rather than "21.52".
 *
 * The scraper's own shape is still accepted verbatim by `loadBoxScores`, because a commissioner
 * loading a fresh scrape should not have to run it through a build step first.
 */
interface PackedFile {
  version: 2;
  source: string;
  names: string[];
  pos: string[];
  teams: string[];
  /** [season, week, side, side] where side is [id, teamIx, ptsCents, flatStarters, flatBench]. */
  games: [number, number, PackedSide, PackedSide][];
}
type PackedSide = [number, number, number | null, (number | null)[], (number | null)[]];

const isPacked = (f: BoxFile | PackedFile): f is PackedFile =>
  (f as PackedFile).version === 2 && Array.isArray((f as PackedFile).names);

/**
 * Expand the bundled form. Exported so a test can put the build script's own output through it —
 * a packer and an unpacker that disagree would fail silently and produce plausible wrong lineups.
 */
export function expandPacked(f: unknown): BoxFile {
  return unpack(f as PackedFile);
}

function unpack(f: PackedFile): BoxFile {
  const un = (n: number | null) => (n === null ? null : n / 100);
  const lines = (flat: (number | null)[]): Line[] => {
    const out: Line[] = [];
    for (let i = 0; i < flat.length; i += 3) {
      out.push([
        f.names[flat[i] as number]!,
        f.pos[flat[i + 1] as number]!,
        un(flat[i + 2] ?? null),
      ]);
    }
    return out;
  };
  const side = (s: PackedSide): BoxSide => ({
    id: s[0], team: f.teams[s[1]]!, pts: un(s[2]), st: lines(s[3]), bn: lines(s[4]),
  });
  return {
    version: 2,
    source: f.source,
    games: f.games.map(([s, w, a, b]) => ({ s, w, a: side(a), b: side(b) })),
  };
}

const rawBundled = bundled as unknown as BoxFile | PackedFile;
/** Expanded once, lazily — a build with no box scores should not pay for the walk. */
let expanded: BoxFile | null = null;
const BUNDLE = (): BoxFile => {
  if (!expanded) expanded = isPacked(rawBundled) ? unpack(rawBundled) : rawBundled;
  return expanded;
};

let LOADED: BoxFile | null = null;
let VERSION = 0;
const watchers = new Set<() => void>();

export const boxVersion = (): number => VERSION;

/**
 * The bundled archive's interned name table, or empty when the archive is not the packed form.
 *
 * Exposed for `boxClubs.ts`, which maps a season and a name INDEX to an NFL club — the club is
 * not in the archive at all, so it is carried in a parallel file keyed by these same indices.
 * Empty is a safe answer: no table, no lookup, no chips.
 */
export function internedNames(): readonly string[] {
  return isPacked(rawBundled) ? rawBundled.names : [];
}

/**
 * Every game the app has, with the version stamped on it.
 *
 * The version travels WITH the games rather than being fetched separately, so a caller building a
 * derived index cannot cache rows from one archive against the version of another — which is
 * exactly the race a `loadBoxScores` mid-session would cause.
 */
export function allBoxGames(): { version: number; games: BoxGame[] } {
  return { version: VERSION, games: all() };
}

export function onBoxesChanged(fn: () => void): () => void {
  watchers.add(fn);
  return () => {
    watchers.delete(fn);
  };
}

/** Accept a scrape file. Returns what it found, or throws with a reason a person can act on. */
export function loadBoxScores(raw: unknown): { games: number; seasons: number[] } {
  const file = raw as BoxFile;
  if (!file || !Array.isArray(file.games)) {
    throw new Error('That file has no games in it — it is not a box-score export.');
  }
  const bad = file.games.find((g) => typeof g.s !== 'number' || !g.a || !g.b);
  if (bad) throw new Error('That file is a box-score export but some games are malformed.');
  LOADED = file;
  VERSION += 1;
  for (const w of watchers) w();
  return {
    games: file.games.length,
    seasons: [...new Set(file.games.map((g) => g.s))].sort((a, b) => a - b),
  };
}

export function clearLoadedBoxScores(): void {
  LOADED = null;
  VERSION += 1;
  for (const w of watchers) w();
}

const all = (): BoxGame[] => [...(BUNDLE().games ?? []), ...(LOADED?.games ?? [])];

let index: { v: number; map: Map<string, BoxGame> } | null = null;

const key = (season: number, week: number, team: string) =>
  `${season}|${week}|${team.replace(/\s+/g, ' ').trim().toLowerCase()}`;

function indexed(): Map<string, BoxGame> {
  if (index && index.v === VERSION) return index.map;
  const map = new Map<string, BoxGame>();
  for (const g of all()) {
    // Indexed by BOTH team names, because a caller has one game and either side may be the one
    // it knows about.
    map.set(key(g.s, g.w, g.a.team), g);
    map.set(key(g.s, g.w, g.b.team), g);
  }
  index = { v: VERSION, map };
  return map;
}

export interface BoxLookup {
  game: BoxGame;
  /** The side matching the team asked for, and the other one. */
  mine: BoxSide;
  theirs: BoxSide;
  /** False when the starters do not add up to the total Yahoo printed. */
  totalsAgree: boolean;
}

/** The box score for one team in one week, if it has been collected. */
export function boxFor(season: number, week: number, team: string): BoxLookup | null {
  const game = indexed().get(key(season, week, team));
  if (!game) return null;
  const match = key(game.s, game.w, game.a.team) === key(season, week, team);
  const mine = match ? game.a : game.b;
  const theirs = match ? game.b : game.a;
  return { game, mine, theirs, totalsAgree: sideAdds(mine) && sideAdds(theirs) };
}

/** Do the starters add up to the printed total? Within a cent, for rounding. */
export function sideAdds(s: BoxSide): boolean {
  if (s.pts === null) return false;
  const sum = s.st.reduce((n, l) => n + (l[2] ?? 0), 0);
  return Math.abs(sum - s.pts) < 0.011 || Math.abs(Math.round(sum) - Math.round(s.pts)) < 1.01;
}

/** Points left on the bench: what the bench scored, against what the starters did. */
export function benchPoints(s: BoxSide): { bench: number; started: number } {
  return {
    bench: round(s.bn.reduce((n, l) => n + (l[2] ?? 0), 0)),
    started: round(s.st.reduce((n, l) => n + (l[2] ?? 0), 0)),
  };
}

/* -- what the scrape brought with it --------------------------------------------------------- */

/*
 * Yahoo's matchup page puts the player's name, a "Video Forecast" link, a notes icon and that
 * week's NFL result in one cell, and the scrape took the cell. So the board reads
 * `Brock PurdyVideo Forecast Final W 42-38 vs Chi` where it means `Brock Purdy`.
 *
 * The NFL result is not junk and is not thrown away — it goes on the row's hint, where somebody
 * asking "what did his team do that week" can still get it. The link text and the icon are junk
 * and go entirely.
 */

/** Yahoo's own UI furniture, glued straight onto the end of the name with no separator. */
const CHROME = /\s*Video\s*Forecast\s*/gi;
/**
 * The icon characters the scrape picked up. Yahoo's own icon font lives in the PRIVATE USE
 * AREA — U+E03E is in this archive 3,414 times — which renders as an empty box in any font that
 * is not Yahoo's, so it was never anything but noise here.
 */
const GLYPHS = /[\uE000-\uF8FF\u25A0-\u25FF\u2600-\u27BF\uFE0F]/g;
/** Where the NFL result starts. `Final W 42-38 vs Chi`, `Bye`, `Sun 1:00 pm`. */
const RESULT = /\s*\b(Final\b.*|Bye\b.*|Q[1-4]\b.*|OT\b.*)$/;

export interface BoxName {
  /** What to print. */
  name: string;
  /** That week's NFL game, if the cell carried one. Goes on the hint, not the row. */
  game: string | null;
}

/** An unfilled roster slot. Not a player, so it has no score and never gets a zero. */
export const EMPTY_SLOT = '(Empty)';

export function splitBoxName(raw: string): BoxName {
  let s = raw.replace(CHROME, ' ').replace(GLYPHS, ' ').replace(/\s+/g, ' ').trim();
  const m = s.match(RESULT);
  const game = m ? m[1]!.trim() : null;
  if (m) s = s.slice(0, m.index).trim();
  // Never return nothing: a cell that was ONLY furniture keeps whatever it originally said.
  return { name: s || raw.trim(), game };
}

/**
 * WHETHER A BLANK ON THIS SIDE MEANS ZERO OR MEANS UNKNOWN.
 *
 * The archive is full of blank player scores and they look like missing data. Mostly they are not:
 * Yahoo printed an empty cell for a player who scored nothing, and the proof is that the starters
 * WITH numbers already add up to the team total Yahoo printed independently. Where they add up,
 * every blank on that side is a zero and should be drawn as `0.00` — the house rule is that blank
 * means unknown, so drawing a known zero as blank is the app lying in the other direction.
 *
 * Where they do NOT add up, something really is missing and the blanks stay blank.
 *
 * Judged on the STARTERS, because the printed total is the only figure this app did not derive
 * itself — the bench total is computed from the very lines in question and cannot check them. The
 * verdict then applies to the whole side, which is the honest generalisation: one scrape, one
 * convention for an empty cell.
 */
export function blanksAreZero(s: BoxSide): boolean {
  if (s.pts === null) return false;
  if (!s.st.some((l) => l[2] === null)) return true;
  return sideAdds(s);
}

/** The points to print for a line, given that verdict. `null` means genuinely unknown. */
export function linePoints(l: Line, zeroed: boolean): number | null {
  if (l[2] !== null) return l[2];
  // An empty bench slot is not a player who scored nothing; it is nobody. It stays blank whatever
  // the rest of the side reconciles to.
  if (splitBoxName(l[0]).name === EMPTY_SLOT) return null;
  return zeroed ? 0 : null;
}

/* -- what position was he? -------------------------------------------------------------------
 *
 * The archive stores the LINEUP SLOT, not the player's position, and for two of the nine slots
 * those are different questions: `BN` says only that he was benched, and `W/R/T` says only that
 * the flex was used. So a chip drawn straight off the slot would leave half of every box score
 * unlabelled — which is most of what a reader wants labelled, because the bench is where the
 * "why did he start him over him" arguments live.
 *
 * The answer is already in the archive: a man benched in week 16 started somewhere in week 3.
 * So the position is learned from every OTHER appearance the same player makes, across 1,770
 * games. Nothing is invented — a player who only ever appears in a flex or on a bench gets no
 * chip, which is the honest outcome and is rare.
 *
 * NOT the current NFL index. That knows where Waddle plays in 2026 and says nothing true about a
 * 2006 box score, and half the names in this archive retired before it was written.
 */

/** Slots that name a position. `BN` and `W/R/T` do not, and `D` is Yahoo's older DEF label. */
const POSITIONAL = new Set(['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'D']);

/** Yahoo wrote defenses as `D` for the early seasons and `DEF` later. One chip, either way. */
const canonPos = (slot: string) => (slot === 'D' ? 'DEF' : slot);

let posIndex: { v: number; map: Map<string, string> } | null = null;

function positions(): Map<string, string> {
  if (posIndex && posIndex.v === VERSION) return posIndex.map;
  const counts = new Map<string, Map<string, number>>();
  for (const g of all()) {
    for (const side of [g.a, g.b]) {
      for (const l of [...side.st, ...side.bn]) {
        const slot = canonPos(l[1]);
        if (!POSITIONAL.has(slot)) continue;
        const k = splitBoxName(l[0]).name.toLowerCase();
        const by = counts.get(k) ?? new Map<string, number>();
        by.set(slot, (by.get(slot) ?? 0) + 1);
        counts.set(k, by);
      }
    }
  }
  const map = new Map<string, string>();
  for (const [k, by] of counts) {
    // Most-started wins. A tie goes alphabetically so the answer is stable across reloads rather
    // than depending on which game the scrape happened to write first.
    const best = [...by.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    if (best) map.set(k, best[0]);
  }
  posIndex = { v: VERSION, map };
  return map;
}

/**
 * The position to chip a line with, or `null` for no chip.
 *
 * THE PLAYER'S POSITION, NOT THE SLOT HE WAS PUT IN — and the archive answers that better than
 * any single row can. Yahoo's eligibility drifts: Marques Colston is a WR in most weeks of this
 * archive and a TE in some of them, and a chip read off each row would flicker between the two
 * down a single box score. So the position is whatever this player is MOST OFTEN across all 1,770
 * games, which gives one answer per player and the same answer on every screen he appears on.
 *
 * The row's own slot is only the fallback, for a player who somehow never appears in a positional
 * slot anywhere. That is rare, and it keeps a starter chipped rather than bare.
 */
export function linePosition(l: Line): string | null {
  const { name } = splitBoxName(l[0]);
  if (name === EMPTY_SLOT) return null;
  const learned = positions().get(name.toLowerCase());
  if (learned) return learned;
  const slot = canonPos(l[1]);
  return POSITIONAL.has(slot) ? slot : null;
}

const round = (n: number) => Math.round(n * 100) / 100;

export interface BoxCoverage {
  games: number;
  seasons: number[];
  bundledGames: number;
  loadedGames: number;
  source: string;
}

/** What the app actually has, for a screen that has to be honest about the gap. */
export function boxCoverage(): BoxCoverage {
  return {
    games: all().length,
    seasons: [...new Set(all().map((g) => g.s))].sort((a, b) => a - b),
    bundledGames: BUNDLE().games?.length ?? 0,
    loadedGames: LOADED?.games?.length ?? 0,
    source: LOADED?.source ?? BUNDLE().source ?? '',
  };
}
